import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { ButtonV2 } from "./ui/ButtonV2";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import { initAnalyticsIfConsented } from "@/lib/analytics";

const COOKIE_CATEGORIES = [
  {
    key: "necessary",
    label: "Necessary Cookies",
    description:
      "Essential for the website to function. These cannot be disabled.",
    required: true,
  },
  {
    key: "analytics",
    label: "Analytics Cookies",
    description:
      "Help us understand how visitors use our site through anonymous analytics.",
    required: false,
  },
  {
    key: "marketing",
    label: "Marketing Cookies",
    description:
      "Used to deliver relevant content and measure campaign effectiveness.",
    required: false,
  },
];

export function CookieConsentV2() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  const dialogRef = useRef(null);
  const bannerRef = useRef(null);
  useFocusTrap(showPreferences, dialogRef);

  useEffect(() => {
    if (!showPreferences) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setShowPreferences(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [showPreferences]);

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
    try {
      setPreferences(JSON.parse(consent));
      initAnalyticsIfConsented();
    } catch {
      /* ignore invalid stored consent */
    }
  }, []);

  const save = (prefs) => {
    setPreferences(prefs);
    localStorage.setItem("cookieConsent", JSON.stringify(prefs));
    setShowBanner(false);
    setShowPreferences(false);
    if (prefs.analytics) {
      initAnalyticsIfConsented();
    }
  };

  const handleAcceptAll = () =>
    save({ necessary: true, analytics: true, marketing: true });

  const handleAcceptNecessary = () =>
    save({ necessary: true, analytics: false, marketing: false });

  const handlePreferenceChange = (key) => {
    if (key === "necessary") return;
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  return (
    <>
      <div
        ref={bannerRef}
        role="region"
        aria-label="Cookie consent"
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 bg-v2-surface border-t border-v2-border shadow-v2-lg"
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie
                className="w-6 h-6 text-brand flex-shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <div className="flex-1">
                <h2 className="text-base font-semibold text-v2-foreground mb-2">
                  We value your privacy
                </h2>
                <p className="text-sm text-v2-muted">
                  We use cookies to enhance your experience and analyze traffic.{" "}
                  <Link
                    to="/legal/cookies"
                    className="text-brand hover:underline"
                  >
                    Learn more
                  </Link>
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <ButtonV2
                variant="secondary"
                size="default"
                onClick={() => setShowPreferences(true)}
              >
                Customize
              </ButtonV2>
              <ButtonV2
                variant="secondary"
                size="default"
                onClick={handleAcceptNecessary}
              >
                Necessary Only
              </ButtonV2>
              <ButtonV2 size="default" onClick={handleAcceptAll}>
                Accept All
              </ButtonV2>
            </div>
          </div>
        </div>
      </div>

      {showPreferences && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowPreferences(false)}
          role="presentation"
        >
          <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cookie-dialog-title"
            aria-describedby="cookie-dialog-desc"
            className="bg-v2-surface rounded-v2-xl shadow-v2-lg max-w-2xl w-full max-h-[90dvh] overflow-y-auto border border-v2-border"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 sm:p-6 border-b border-v2-border">
              <div className="flex items-center gap-3">
                <Cookie className="w-6 h-6 text-brand" aria-hidden="true" />
                <h2
                  id="cookie-dialog-title"
                  className="text-xl font-semibold text-v2-foreground"
                >
                  Cookie Preferences
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setShowPreferences(false)}
                className="v2-touch-target flex items-center justify-center p-2 rounded-v2-md hover:bg-v2-background transition-colors"
                aria-label="Close cookie preferences"
              >
                <X className="w-5 h-5 text-v2-muted" />
              </button>
            </div>

            <div className="p-5 sm:p-6 space-y-4">
              <p id="cookie-dialog-desc" className="text-sm text-v2-muted">
                Choose which cookies you allow. Necessary cookies are always
                enabled.
              </p>

              {COOKIE_CATEGORIES.map((category) => {
                const isOn = preferences[category.key];
                return (
                  <div
                    key={category.key}
                    className="border border-v2-border rounded-v2-lg p-4"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h3 className="font-medium text-v2-foreground mb-1">
                          {category.label}
                        </h3>
                        <p className="text-sm text-v2-muted">
                          {category.description}
                        </p>
                      </div>
                      {category.required ? (
                        <span
                          className="text-xs font-medium text-brand whitespace-nowrap"
                          aria-label="Always enabled"
                        >
                          Always on
                        </span>
                      ) : (
                        <button
                          type="button"
                          role="switch"
                          aria-checked={isOn}
                          aria-label={`${category.label}: ${isOn ? "enabled" : "disabled"}`}
                          onClick={() => handlePreferenceChange(category.key)}
                          className={`w-12 h-7 min-w-[48px] rounded-full flex items-center px-1 transition-colors touch-manipulation ${
                            isOn ? "bg-brand" : "bg-v2-border"
                          }`}
                        >
                          <span
                            className={`block w-5 h-5 bg-white rounded-full shadow transition-transform ${
                              isOn ? "translate-x-5" : "translate-x-0"
                            }`}
                            aria-hidden="true"
                          />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 p-5 sm:p-6 border-t border-v2-border">
              <ButtonV2 variant="secondary" onClick={handleAcceptNecessary}>
                Necessary Only
              </ButtonV2>
              <ButtonV2 onClick={() => save(preferences)}>
                Save Preferences
              </ButtonV2>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
